import * as React from "react";
import { useState, createContext, useContext, useCallback, useEffect } from "react";
import { fetchFrames, fetchMediums } from "../../utils/artwork-utils";
import { CURRENT_STATE_PHYSICAL_WORK, DIGTALIZATION_TECHNIQUE } from "../constants/artworks";
import { useForm } from "react-hook-form";
import { generateMediaThumbnail, processThumbnail } from "../../utils/thumbnails";
import { getCookieToken } from "../../utils/get-auth-user";
import { guessContentType, proxiedUrlFor } from "../../utils/media-url-utils";
import configs from "../../utils/configs";
import { ArtworkInfo, convertDimensionsInchesToMeters, getModelOnly } from "../../utils/artwork-model-utils";
import { ArtworkHelper } from "../../singleton/ArtworkHelper";
import { extractUUID, generateAndUploadThumbnailUrl, getMediaType, getUrlExtension } from "../../utils/custom-utils";
import { UPLOAD_DRAWER_TOGGLER_ID, useToolbarContext } from "./ToolbarContext";
import { useObjectSettings } from "./ObjectSettingsProvider";
import { findAncestorWithComponent } from "../../utils/scene-graph";
import notificationsCenter from "../../singleton/NotificationsCenter";
import { useObjectList } from "../room/useObjectList";
import { isThreeMeshUIValidInput, isYearValid } from "../../utils/three-mesh-ui-utils";
import { createShortID, updateARUrl } from "../../utils/artwork-qr-utils";

const UploadFormContext = createContext();

// Create a custom hook to use the UploadFormContext
export const useUploadFormContext = () => useContext(UploadFormContext);

export const artworkFields = {
  url: "url",
  file: "file",
  href: "href",
  title: "title",
  hideHref: "hideHref",
  artName: "artName",
  description: "description",
  medium: "medium",
  dimensions: "dimensions",
  dimensionsY: "dimensionsY",
  dimensionsX: "dimensionsX",
  dimensionsZ: "dimensionsZ",
  artistName: "artistName",
  artistStatement: "artistStatement",
  artistBio: "artistBio",
  techniqueUsed: "techniqueUsed",
  currentPhysicalWork: "currentPhysicalWork",
  labels: "labels",
  license: "license",
  AIcreation: "workCreatedByAI",
  terms: "terms",
  poligons: "poligons",
  fileWeight: "fileWeight",
  enableARTrigger: "enableARTrigger",
  aiToolUsed: "aiToolUsed",
  unit: "unit",
  myMediaId: "myMediaId",
  declareRights: "declareRights",
  artworkDate: "artworkDate",
  authorDate: "authorDate",
  type: "type",
  frame: "frame",
  customMedium: "customMedium",
  isPublicDomainArt: "isPublicDomainArt"
};

export const ARTWORK_TYPES = {
  DIGITAL_ART: "Digital Art",
  DIGITAL_TWIN_ART: "Digital Twin Art"
};

const artworkUpdateUrl = `${configs["CUSTOM_API_URL"]}/assets360-artwork`;

export function useUploadForm(scene) {
  const [isDigitalArt, setIsDigitalArt] = useState(false);
  const [isDigitalTwinArt, setIsDigitalTwinArt] = useState(false);

  useEffect(() => {
    if (isDigitalArt) {
      setValue(artworkFields.type, ARTWORK_TYPES.DIGITAL_ART);
    } else if (isDigitalTwinArt) {
      setValue(artworkFields.type, ARTWORK_TYPES.DIGITAL_TWIN_ART);
    }
  }, [isDigitalArt, isDigitalTwinArt]);

  const { selectedObject } = useObjectSettings();
  const { deselectObject } = useObjectList();

  const { handleSubmit, register, watch, setValue } = useForm();
  const [thumbnail, setThumbnail] = useState("");
  const [indefiniteLoading, setIndefiniteLoading] = useState(false);
  const [mediums, setMedium] = useState([]);
  const [frames, setFrame] = useState([]);
  const [sizes, setSizes] = useState(null);
  const [myMediaAsset, setAssetMyMedia] = useState(null);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [errorUploadUrl, setErrorUploadUrl] = useState(null);
  const [errorUpload, setErrorUpload] = React.useState(false);
  const [disabledSubmit, setDisabledSubmit] = React.useState(true);
  const [hasInvalidFields, setHasInvalidFields] = React.useState(false);
  const file = watch(artworkFields.file);
  const url = watch(artworkFields.url);

  const hasFile = file && file.length > 0;

  const hasFileOrURL = hasFile || url;

  // digitalTwin art required fields
  const artTitle = watch(artworkFields.artName);
  const medium = watch(artworkFields.medium);
  const artistName = watch(artworkFields.artistName);
  const license = watch(artworkFields.license);
  const terms = watch(artworkFields.terms);
  const declareRights = watch(artworkFields.declareRights);
  const aiToolUsed = watch(artworkFields.aiToolUsed);
  const bio = watch(artworkFields.artistBio);
  const statement = watch(artworkFields.artistStatement);
  const labels = watch(artworkFields.labels);
  const title = watch(artworkFields.title);
  const description = watch(artworkFields.description);
  const artDate = watch(artworkFields.artworkDate);

  useEffect(() => {
    const fieldsToValidate = [title, aiToolUsed, bio, statement, labels, artTitle, description];
    let fieldInvalid = false;
    fieldsToValidate.forEach(field => {
      if (field && !isThreeMeshUIValidInput(field)) {
        fieldInvalid = true;
      }
    });

    if (!isYearValid(artDate)) {
      fieldInvalid = true;
    }

    setHasInvalidFields(fieldInvalid);
  }, [title, aiToolUsed, bio, statement, labels, artTitle, description, artDate]);

  const digitalTwinEnable = [
    artTitle,
    artistName,
    license && license != "--",
    terms,
    hasFileOrURL,
    declareRights,
    artDate,
    !loadingUpload
  ].every(item => Boolean(item));

  // digital Art required fields
  const digitalArtEnable = [
    artTitle,
    artistName,
    license,
    terms,
    hasFileOrURL,
    declareRights,
    artDate,
    !loadingUpload
  ].every(item => Boolean(item));

  const token = getCookieToken();

  const handleOpenMediaModal = useCallback(
    e => {
      // Entry is = e.detail, entry has url or file, we need to set url or file
      if (e.detail.url) {
        // We need to update the artworkfields with the url
        setValue(artworkFields.url, e.detail.url);
        setValue(artworkFields.file, null);
        window.dispatchEvent(new CustomEvent(`open-${UPLOAD_DRAWER_TOGGLER_ID}`));
      }

      if (e.detail.file) {
        // We need to update the artworkfields with the file
        setValue(artworkFields.url, null);
        setValue(artworkFields.file, e.detail.file);
        window.dispatchEvent(new CustomEvent(`open-${UPLOAD_DRAWER_TOGGLER_ID}`));
      }
    },
    [setValue]
  );

  // Add event listener that hears "open-media-modal" event
  useEffect(() => {
    window.addEventListener("open-media-modal", handleOpenMediaModal);
    return () => {
      window.removeEventListener("open-media-modal", handleOpenMediaModal);
    };
  }, [handleOpenMediaModal]);

  useEffect(() => {
    const updateDisabledStatus = () => {
      if (!isDigitalTwinArt && !isDigitalArt) {
        setDisabledSubmit(!hasFileOrURL || !title);
      } else if (isDigitalTwinArt) {
        setDisabledSubmit(!digitalTwinEnable || hasInvalidFields);
      } else {
        setDisabledSubmit(!digitalArtEnable || hasInvalidFields);
      }
    };

    updateDisabledStatus();
  }, [
    hasInvalidFields,
    title,
    selectedObject,
    artDate,
    file,
    url,
    isDigitalTwinArt,
    isDigitalArt,
    artTitle,
    medium,
    artistName,
    license,
    terms,
    declareRights,
    hasFileOrURL,
    digitalTwinEnable,
    digitalArtEnable,
    loadingUpload
  ]);

  const onErrorUrlUploadChange = useCallback(error => {
    setErrorUploadUrl(error);
  }, []);

  const onClose = useCallback(() => {
    setIndefiniteLoading(false);
    onErrorUrlUploadChange("");
    // use window event listener
    window.dispatchEvent(new CustomEvent(`toggle-${UPLOAD_DRAWER_TOGGLER_ID}`));
    onClear();
  }, [onErrorUrlUploadChange]);

  const digitalizationSelect = [
    {
      label: "--",
      value: "--"
    }
  ].concat(
    DIGTALIZATION_TECHNIQUE.map(technique => ({
      value: technique,
      label: technique
    }))
  );

  const currentStateSelect = [
    {
      label: "--",
      value: "--"
    }
  ].concat(
    CURRENT_STATE_PHYSICAL_WORK.map(work => ({
      value: work,
      label: work
    }))
  );

  React.useEffect(() => {
    Object.values(artworkFields).forEach(field => register(field));
  }, [register]);

  const setMediums = async () => {
    try {
      let data = await fetchMediums();
      if (data && data.length > 0) {
        data = [
          { name: "--", model: null },
          { name: "Digital File", model: "Digital File" },
          { name: "Custom", model: "Custom" },
          ...data
        ];
      } else {
        data = [
          { name: "--", model: null },
          { name: "Digital File", model: "Digital File" },
          { name: "Custom", model: "Custom" }
        ];
      }
      setMedium(data);
    } catch (error) {
      console.error("Failed to fetch mediums:", error);
      setMedium([
        { name: "--", model: null },
        { name: "Digital File", model: "Digital File" },
        { name: "Custom", model: "Custom" }
      ]);
    }
  };

  const setFrames = async () => {
    try {
      let data = await fetchFrames();
      if (data && data.length > 0) {
        data = [{ name: "--", model: null }, ...data];
      } else {
        data = [{ name: "--", model: null }];
      }
      setFrame(data);
    } catch (error) {
      console.error("Failed to fetch mediums:", error);
      setFrame([{ name: "--", model: null }]);
    }
  };

  const getSizes = async () => {
    let x, y, z;
    if (artwork.el.components["media-image"]) {
      const scale = artwork.el.components["media-image"]?.mesh.scale;
      const { xI, yI, zI } = convertDimensionsMetersToInches(scale.x, scale.y, scale.z);
      x = xI;
      y = yI;
      z = zI;
    } else {
      const scale = artwork.el.object3DMap.mesh.scale;
      const { xI, yI, zI } = convertDimensionsMetersToInches(scale.x, scale.y, scale.z);
      x = (xI * 1.5).toFixed(2);
      y = (yI * 1.5).toFixed(2);
      z = (zI * 1.5).toFixed(2);
    }

    setSizes({ x, y, z });
  };

  const onThumbnailChange = useCallback(thumbnail => setThumbnail(thumbnail), []);

  const processThumbnailForm = useCallback(
    async (entry, fileUrl) => {
      if (getMediaType(fileUrl) == "audio" || getMediaType(fileUrl) == "video") {
        setThumbnail(fileUrl);
      } else {
        const blob = await processThumbnail(entry, fileUrl, { returnBlob: true });

        let urlFile = URL.createObjectURL(blob);
        setThumbnail(urlFile);

        const thumbnailFile = new File([blob], `${entry?.name.split(".").shift()}-thumb.png`, { type: "image/png" });

        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(thumbnailFile);
        setValue("thumbnail", dataTransfer.files);
      }
    },
    [setValue]
  );

  const onSubmitArtworkProperties = useCallback(
    async data => {
      let {
        [artworkFields.title]: title,
        [artworkFields.url]: url,
        [artworkFields.href]: href,
        [artworkFields.hideHref]: hideHref,
        [artworkFields.artistName]: artistName,
        [artworkFields.artistStatement]: artistStatement,
        [artworkFields.description]: description,
        [artworkFields.dimensionsX]: dimensionsX,
        [artworkFields.dimensionsY]: dimensionsY,
        [artworkFields.dimensionsZ]: dimensionsZ,
        [artworkFields.artName]: artName,
        [artworkFields.unit]: unit,
        [artworkFields.medium]: medium,
        [artworkFields.myMediaId]: myMediaId,
        [artworkFields.enableARTrigger]: enableARTrigger,
        [artworkFields.labels]: labels,
        [artworkFields.frame]: frame,
        [artworkFields.customMedium]: customMedium,
        [artworkFields.isPublicDomainArt]: isPublicDomainArt
      } = data;

      const myMediaUrl = `${configs["CUSTOM_API_URL"]}/assets360`;
      setLoadingUpload(true);

      //clean values
      if (medium?.label == "--" || medium == "--") {
        medium = null;
      }

      let artwork = null;
      let fileUrl = selectedObject.el.components["media-loader"]?.data?.src;
      const imageTexture = selectedObject?.el?.components["gltf-model-plus"]?.model?.userData?.metadata?.imageTexture;
      const ancestorEl = findAncestorWithComponent(selectedObject.el, "media-loader");
      const oldArtworkData = selectedObject.el?.components["artwork"]?.data || {};
      const mediaType = getMediaType(fileUrl);
      let imageUrl = imageTexture ? imageTexture : oldArtworkData?.image || mediaType == "image" ? fileUrl : null;

      let glbModel = mediaType == "model" ? fileUrl : "";
      ``;
      let mediumUrl = mediums.find(m => m.name === (medium?.label || medium))?.model;
      mediumUrl = mediumUrl == "Digital File" || mediumUrl == "Custom" ? null : mediumUrl;

      let frameUrl = frame?.model || frames.find(f => f.name === frame)?.model || "";

      const glbToUse =
        (oldArtworkData?.image || mediaType == "image" || imageTexture) && ancestorEl
          ? mediumUrl
            ? mediumUrl
            : null
          : glbModel;

      //create qr ID
      const qrCodeId = await createShortID(fileUrl);

      artwork = await ArtworkHelper.getInstance().generateArtworkData({
        ...data,
        mediums,
        imageUrl,
        fileUrl,
        glbModel: glbToUse,
        frameUrl,
        qrCodeId,
        mediaType,
        isPublicDomainArt
      });
      const artworkInfo = artwork.artworkInfo;

      //update element in QR
      await updateARUrl(artwork?.artwork_model, qrCodeId);

      if (!medium) {
        artworkInfo.medium = "";
      }

      if (frameUrl) {
        const urlResult = new URL(frameUrl);
        const params = new URLSearchParams(url.search);

        // Check if the query parameter 'unique' exists
        if (!params.has("unique")) {
          // Add the 'unique' query parameter with a unique value (e.g., timestamp)
          params.set("unique", Date.now());
          urlResult.search = params.toString();
        }

        frameUrl = urlResult.toString();
      }

      if (mediaType == "model" && !oldArtworkData?.image && !imageTexture && ancestorEl) {
        artworkInfo.glbModel = artwork?.artwork_model;
        selectedObject.el.removeAttribute("artwork");
        selectedObject.el.setAttribute("artwork", {
          ...oldArtworkData,
          artworkInfo,
          glbModel: artwork?.artwork_model,
          completed: false
        });
        ancestorEl.components["media-loader"].data.version = ancestorEl.components["media-loader"].data.version + 1;
        ancestorEl.components["media-loader"].data.src = artwork?.artwork_model;
      } else if ((oldArtworkData?.image || mediaType == "image" || imageTexture) && ancestorEl) {
        let imageUrl = oldArtworkData?.image || fileUrl;

        if (medium && mediumUrl) {
          glbModel = medium?.model;
        } else if (medium && !mediumUrl) {
          glbModel = null;
        }

        if (medium?.label) {
          artworkInfo.medium = medium?.label;
        }
        ancestorEl.setAttribute("media-loader", { src: artwork?.artwork_model });
      }

      const uuid = extractUUID(imageUrl || fileUrl);

      if (artwork?.metadata?.myMediaId) {
        artwork.myMediaId = artwork?.metadata?.myMediaId;
      }

      if (uuid) {
        artwork.uuid = uuid;
      }

      // Update DB Properties
      if (artwork) {
        if (artwork?.metadata) {
          delete artwork.metadata.file;

          // Remove undefined values from metadata
          Object.keys(artwork.metadata).forEach(key => {
            if (artwork.metadata[key] === undefined) {
              delete artwork.metadata[key];
            }
          });
          artwork.metadata = JSON.stringify(artwork.metadata);
        }

        await fetch(`${artworkUpdateUrl}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
            authorization: `Bearer ${token}`
          },
          body: JSON.stringify(artwork)
        });
      }
      setTimeout(() => {
        setLoadingUpload(false);
        deselectObject();
        window.dispatchEvent(new CustomEvent("close-object-settings"));
      }, 4000);
    },
    [scene, selectedObject]
  );

  const onSubmitForm = useCallback(
    async data => {
      const {
        [artworkFields.title]: title,
        [artworkFields.file]: file,
        [artworkFields.url]: url,
        [artworkFields.href]: href,
        [artworkFields.hideHref]: hideHref,
        [artworkFields.artistName]: artistName,
        [artworkFields.artistStatement]: artistStatement,
        [artworkFields.description]: description,
        [artworkFields.dimensionsX]: dimensionsX,
        [artworkFields.dimensionsY]: dimensionsY,
        [artworkFields.dimensionsZ]: dimensionsZ,
        [artworkFields.artName]: artName,
        [artworkFields.unit]: unit,
        [artworkFields.medium]: medium, //returns object with value and label
        [artworkFields.myMediaId]: myMediaId,
        [artworkFields.enableARTrigger]: enableARTrigger,
        [artworkFields.labels]: labels,
        [artworkFields.frame]: frame
      } = data;
      const myMediaUrl = `${configs["CUSTOM_API_URL"]}/assets360`;
      setLoadingUpload(true);

      try {
        let fileUrl;
        if (file && file.length) {
          const acceptedFile = file[0];
          fileUrl = acceptedFile.downloableUrl;
        } else {
        }
        let artwork = null;
        const mediaType = getMediaType(fileUrl);
        if (artistName) {
          let glbModel = mediaType == "model" ? fileUrl : "";
          if (mediaType == "image" && medium && medium?.name != "Digital File" && medium?.name != "Custom") {
            glbModel = medium?.model;
          }

          const frameUrl = frame?.model;

          //create qr ID
          const qrCodeId = await createShortID(fileUrl);

          artwork = await ArtworkHelper.getInstance().generateArtworkData({
            ...data,
            fileUrl,
            imageUrl: mediaType == "image" ? fileUrl : null,
            mediums,
            medium: medium?.label,
            glbModel,
            frameUrl,
            mediaType,
            qrCodeId
          });

          //update element in QR
          await updateARUrl(artwork?.artwork_model, qrCodeId);
        }

        const updateEntry = {
          title: artName || title,
          description,
          enable_ar_trigger: enableARTrigger,
          labels: labels
        };

        await fetch(`${myMediaUrl}/${myMediaId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
            authorization: `Bearer ${token}`
          },
          body: JSON.stringify(updateEntry)
        });

        scene.emit("add_media", {
          src: artwork?.artwork_model || fileUrl || (file && file.length > 0 && file[0]) || url,
          href,
          hideHref,
          smoothTransition: false,
          fileIsOwned: true
        });

        if (artwork) {
          const uuid = extractUUID(fileUrl);
          if (artwork?.metadata?.myMediaId) {
            artwork.myMediaId = artwork?.metadata?.myMediaId;
          }

          if (uuid) {
            artwork.uuid = uuid;
          }

          if (artwork?.metadata) {
            delete artwork.metadata.file;
            delete artwork.metadata.myMediaId;

            // Remove undefined values from metadata
            Object.keys(artwork.metadata).forEach(key => {
              if (artwork.metadata[key] === undefined) {
                delete artwork.metadata[key];
              }
            });
            artwork.metadata = JSON.stringify(artwork.metadata);
          }

          await fetch(`${artworkUpdateUrl}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              accept: "application/json",
              authorization: `Bearer ${token}`
            },
            body: JSON.stringify(artwork)
          });
        }
        setLoadingUpload(false);
        onClose();
      } catch (error) {
        console.error("Failed to upload media:", error);
        setLoadingUpload(false);
        notificationsCenter.error("Failed to upload asset, please try again");
      }
    },
    [scene]
  );

  const onClear = useCallback(() => {
    setAssetMyMedia(null);
    setThumbnail("");
    setLoadingUpload(false);
    Object.values(artworkFields).forEach(field => setValue(field, ""));
  }, [setValue, setAssetMyMedia, setThumbnail]);

  const onChangeForm = useCallback(
    (e, field, type = "text", value = "") => {
      if (field === "url" && hasFile) {
        return;
      }

      if (type == "value") {
        setValue(field, value);
        return;
      }

      setValue(field, type === "toggle" ? e.target.checked : e.target.value);
    },
    [hasFile, setValue]
  );

  const handleFileLoadedDropzone = useCallback(
    async (files, downloadUrl, myMediaAsset) => {
      setIndefiniteLoading(false);
      files[0].downloableUrl = downloadUrl;
      setValue(artworkFields.file, files);
      setValue(artworkFields.myMediaId, myMediaAsset?.data.id);

      await processThumbnailForm(files[0], downloadUrl);
      onErrorUrlUploadChange("");
    },
    [onErrorUrlUploadChange]
  );

  return {
    mediums,
    setMediums,
    frames,
    setFrames,
    getSizes,
    digitalizationSelect,
    currentStateSelect,
    thumbnail,
    onThumbnailChange,
    formSetValue: setValue,
    formRegister: register,
    processThumbnail: processThumbnailForm,
    onSubmitForm,
    handleSubmit,
    onChangeForm,
    handleFileLoadedDropzone,
    onClear,
    onClose,
    disabledSubmit,
    hasFile,
    loadingUpload,
    setLoadingUpload,
    setThumbnail,
    url,
    indefiniteLoading,
    setIndefiniteLoading,
    onErrorUrlUploadChange,
    errorUploadUrl,
    isDigitalArt,
    isDigitalTwinArt,
    setIsDigitalArt,
    setIsDigitalTwinArt,
    onSubmitArtworkProperties,
    medium,
    errorUpload,
    setErrorUpload,
    file
  };
}

// Create a provider component
export const UploadFormProvider = ({ children, scene }) => {
  const uploadForm = useUploadForm(scene);

  return <UploadFormContext.Provider value={uploadForm}>{children}</UploadFormContext.Provider>;
};
